// Batches in the app: stock per batch, and splitting sale lines across
// batches (first expiry, first out) when a bill is completed.
import {
  allocateFefo,
  allocateProportionally,
  expiryFromShelfLife,
  expiryStatus,
  isWholeDate,
  type DocLineInput,
  type ExpiryStatus,
  type Milli,
  type ShelfLifeUnit,
  type SyncOp,
} from '@ims/core'
import { batchOp, insertOp, newMovement, type RowContext } from './rows'
import type { LocalRepository } from '@ims/local'
import type { RowOf } from '@ims/data'

export type BatchRow = RowOf<'batches'>

export interface BatchWithStock {
  batch: BatchRow
  /** At the location asked for (or everywhere). */
  qtyMilli: Milli
}

/** Each product's batches with their stock, earliest expiry first. */
export async function loadBatchStock(
  repo: Pick<LocalRepository, 'list' | 'stock'>,
  productIds: readonly string[],
  locationId: string | null = null,
): Promise<Map<string, BatchWithStock[]>> {
  const out = new Map<string, BatchWithStock[]>()
  if (productIds.length === 0) return out
  const levels = await repo.stock.batchLevels({
    productIds: [...productIds],
    ...(locationId ? { locationId } : {}),
  })
  const qty = new Map<string, number>()
  for (const l of levels) qty.set(l.batch_id, (qty.get(l.batch_id) ?? 0) + l.qty_milli)
  for (const productId of productIds) {
    const rows = (await repo.list('batches', { where: { product_id: productId }, limit: 1000 }))
      .rows
    out.set(
      productId,
      rows
        .map((batch) => ({ batch, qtyMilli: qty.get(batch.id) ?? 0 }))
        .sort(
          (a, b) =>
            (a.batch.expiry_date ?? '9999').localeCompare(b.batch.expiry_date ?? '9999') ||
            a.batch.batch_no.localeCompare(b.batch.batch_no),
        ),
    )
  }
  return out
}

export interface SaleLine extends DocLineInput {
  /** Split across batches at checkout (batch-tracked products). */
  trackBatches?: boolean
}

export interface BatchPlan {
  lines: DocLineInput[]
  /** The invoice lines each cart line became (same order as the input). */
  perLine: DocLineInput[][]
  warnings: string[]
  /** Set when the sale must not go ahead (expired stock with "block"). */
  blocked: string | null
}

/**
 * Turns cart lines into invoice lines: a batch product's line is split across
 * its batches, earliest expiry first. A line with a batch chosen by hand keeps
 * it. Stock no batch covers stays on a line without a batch (negative stock is
 * allowed, as for other products). Expired stock is only used after warning,
 * or not at all when `expiredSale` is "block".
 */
export function planBatchLines(
  lines: readonly SaleLine[],
  stock: ReadonlyMap<string, BatchWithStock[]>,
  today: string,
  expiredSale: 'warn' | 'block',
): BatchPlan {
  const out: DocLineInput[] = []
  const perLine: DocLineInput[][] = []
  const warnings: string[] = []
  let blocked: string | null = null
  // Stock already used by earlier lines of this bill.
  const used = new Map<string, number>()

  for (const line of lines) {
    const before = out.length
    planLine(line)
    perLine.push(out.slice(before))
  }
  return { lines: out, perLine, warnings, blocked }

  function planLine(line: SaleLine): void {
    const { trackBatches, ...rest } = line
    if (!trackBatches || !line.productId || line.trackStock === false) {
      out.push(rest)
      return
    }
    const batches = stock.get(line.productId) ?? []
    if (line.batchId) {
      const chosen = batches.find((b) => b.batch.id === line.batchId)
      if (chosen && expiryStatus(chosen.batch.expiry_date, today, 0) === 'expired') {
        if (expiredSale === 'block') blocked = `Batch ${chosen.batch.batch_no} has expired`
        else warnings.push(`${line.description}: batch ${chosen.batch.batch_no} has expired`)
      }
      used.set(line.batchId, (used.get(line.batchId) ?? 0) + line.qtyMilli)
      out.push(rest)
      return
    }
    const available = batches.map((b) => ({
      batchId: b.batch.id,
      qtyMilli: b.qtyMilli - (used.get(b.batch.id) ?? 0),
      expiryDate: b.batch.expiry_date,
    }))
    let result = allocateFefo(available, line.qtyMilli, today)
    const parts = [...result.allocations]
    if (result.remainderMilli > 0 && result.expiredSkipped) {
      if (expiredSale === 'block') {
        blocked = `${line.description}: only expired stock is left`
      } else {
        const expired = allocateFefo(
          available.filter((b) => b.expiryDate && b.expiryDate < today),
          result.remainderMilli,
          today,
          { includeExpired: true },
        )
        parts.push(...expired.allocations)
        const names = expired.allocations
          .map((a) => batches.find((b) => b.batch.id === a.batchId)?.batch.batch_no)
          .join(', ')
        warnings.push(`${line.description}: selling from expired batch ${names}`)
        result = { ...result, remainderMilli: expired.remainderMilli }
      }
    }
    if (result.remainderMilli > 0) {
      warnings.push(
        `${line.description}: ${result.remainderMilli / 1000} not in any batch (stock goes below zero)`,
      )
      parts.push({ batchId: '', qtyMilli: result.remainderMilli })
    }
    for (const p of parts)
      if (p.batchId) used.set(p.batchId, (used.get(p.batchId) ?? 0) + p.qtyMilli)

    if (parts.length <= 1) {
      out.push({ ...rest, batchId: parts[0]?.batchId || null })
      return
    }
    // Split the line; its discount is shared in proportion to quantity.
    const discounts = allocateProportionally(
      line.discountPaisa ?? 0,
      parts.map((p) => p.qtyMilli),
    )
    parts.forEach((p, i) => {
      out.push({
        ...rest,
        qtyMilli: p.qtyMilli,
        discountPaisa: discounts[i] ?? 0,
        batchId: p.batchId || null,
      })
    })
  }
}

/** Badge tone and text for a batch's expiry. */
export function expiryBadge(
  expiry: string | null,
  today: string,
  alertDays: number,
): { status: ExpiryStatus; tone: 'danger' | 'warning' | 'success' | 'neutral'; label: string } {
  const status = expiryStatus(expiry, today, alertDays)
  switch (status) {
    case 'expired':
      return { status, tone: 'danger', label: 'Expired' }
    case 'soon':
      return { status, tone: 'warning', label: 'Expiring soon' }
    case 'ok':
      return { status, tone: 'success', label: 'OK' }
    default:
      return { status, tone: 'neutral', label: 'No expiry' }
  }
}

// -----------------------------------------------------------------------------
// Expiry from shelf life, and moving stock between batches (docs/plans/batch-split.md)
// -----------------------------------------------------------------------------

/** An expiry box that may follow the mfg date: `auto` while the date was worked out here. */
export interface ExpiryBox {
  expiry: string
  auto: boolean
}

/**
 * The expiry after the mfg date or the shelf life changed. It fills an empty box, or one it filled itself,
 * once the mfg date is whole (never from a year still being typed); an expiry typed by hand
 * stays. Typing in the expiry box sets `auto: false`.
 */
export function followExpiry(
  mfg: string,
  box: ExpiryBox,
  shelf: { amount: number | null; unit: ShelfLifeUnit | null },
): ExpiryBox {
  // Only these two fields: callers spread the answer over a row that holds more.
  const kept = { expiry: box.expiry, auto: box.auto }
  if (box.expiry && !box.auto) return kept
  // No shelf life any more: an expiry it filled goes with it.
  if (!shelf.amount) return box.auto ? { expiry: '', auto: false } : kept
  if (!isWholeDate(mfg)) return kept
  return { expiry: expiryFromShelfLife(mfg, shelf.amount, shelf.unit), auto: true }
}

/** The product's shelf life as the expiry rule takes it. */
export function shelfOf(product: {
  shelf_life_days: number | null
  shelf_life_unit: ShelfLifeUnit | null
}): { amount: number | null; unit: ShelfLifeUnit | null } {
  return { amount: product.shelf_life_days, unit: product.shelf_life_unit }
}

/**
 * Stock with no batch at each location: the location's stock less its batches' stock. It is
 * never stored (batch_stock leaves movements without a batch out).
 */
export function noBatchLevels(
  levels: readonly { location_id: string; qty_milli: number }[],
  batchLevels: readonly { location_id: string; qty_milli: number }[],
): Map<string, Milli> {
  const out = new Map<string, Milli>()
  for (const l of levels) out.set(l.location_id, (out.get(l.location_id) ?? 0) + l.qty_milli)
  for (const l of batchLevels) out.set(l.location_id, (out.get(l.location_id) ?? 0) - l.qty_milli)
  return out
}

/** A move between batches is a pair of adjustments whose notes say so. */
export const MOVE_NOTE = 'Moved '

export function isBatchMove(m: { type: string; notes: string | null }): boolean {
  return m.type === 'adjustment' && (m.notes?.startsWith(MOVE_NOTE) ?? false)
}

export interface SplitLine {
  batchNo: string
  mfgDate: string
  expiryDate: string
  qtyMilli: Milli | null
}

export interface SplitInput {
  productId: string
  locationId: string
  /** The batch the stock leaves, or null for stock with no batch. */
  from: BatchRow | null
  /** What `from` holds at the location. */
  availableMilli: Milli
  lines: readonly SplitLine[]
  /** The product's batches, to reuse one whose number is typed again. */
  batches: readonly BatchRow[]
}

/** Why a split cannot be saved yet, in the words the dialog shows; null when it can. */
export function splitProblem(input: SplitInput): string | null {
  const lines = input.lines.filter((l) => l.batchNo.trim() || l.qtyMilli)
  if (lines.length === 0) return 'Add a batch to move stock into.'
  const seen = new Set<string>()
  for (const l of lines) {
    const no = l.batchNo.trim().toLowerCase()
    if (!no) return 'Every line needs a batch number.'
    if (!l.qtyMilli || l.qtyMilli <= 0) return `Enter how many go into ${l.batchNo.trim()}.`
    if (input.from && no === input.from.batch_no.toLowerCase())
      return `${l.batchNo.trim()} is where the stock comes from.`
    if (seen.has(no)) return `${l.batchNo.trim()} is on two lines.`
    seen.add(no)
  }
  const placed = lines.reduce((a, l) => a + (l.qtyMilli ?? 0), 0)
  if (placed > input.availableMilli) return 'That is more than there is to move.'
  return null
}

/**
 * The ops that move stock from one batch (or none) into others, for one commit: the new
 * batches, then for each line an adjustment out of the source and one into the batch. The
 * product's total never changes. A new batch keeps the source batch's cost.
 */
export function planBatchSplit(
  ctx: RowContext,
  input: SplitInput,
): { ops: SyncOp[]; movedMilli: Milli } {
  const problem = splitProblem(input)
  if (problem) throw new Error(problem)
  const fromName = input.from?.batch_no ?? 'No batch'
  const ops: SyncOp[] = []
  let movedMilli = 0
  for (const l of input.lines.filter((x) => x.batchNo.trim())) {
    const no = l.batchNo.trim()
    const existing =
      input.batches.find((b) => b.batch_no.toLowerCase() === no.toLowerCase()) ?? null
    const batch = batchOp(
      ctx,
      input.productId,
      {
        batchNo: no,
        mfgDate: l.mfgDate || null,
        expiryDate: l.expiryDate || null,
        mrpPaisa: input.from?.mrp_paisa ?? null,
        costPaisa: input.from?.cost_paisa ?? null,
        supplierId: input.from?.supplier_id ?? null,
      },
      existing,
    )
    if (batch.op) ops.push(batch.op)
    const qty = l.qtyMilli ?? 0
    const move = (qtyMilli: number, batchId: string | null, notes: string) =>
      insertOp(
        'stock_movements',
        newMovement(ctx, {
          product_id: input.productId,
          location_id: input.locationId,
          type: 'adjustment',
          qty_milli: qtyMilli,
          batch_id: batchId,
          notes,
        }),
      )
    ops.push(move(-qty, input.from?.id ?? null, `${MOVE_NOTE}from ${fromName} to ${no}`))
    ops.push(move(qty, batch.id, `${MOVE_NOTE}to batch ${no} (from ${fromName})`))
    movedMilli += qty
  }
  return { ops, movedMilli }
}
